import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'react-bootstrap/lib/Modal';
import { Button as ReactButton } from 'react-bootstrap';
import ButtonGroup from 'react-bootstrap/lib/ButtonGroup';
import DropdownButton from 'react-bootstrap/lib/DropdownButton';
import MenuItem from 'react-bootstrap/lib/MenuItem';
import { Tooltip } from '@material-ui/core';

import { downloadFile, httpGet, httpPost, isUserPermitted } from '../../../utils';
import {
    calcDirtyTabs,
    calcInvalidTabs,
    filterFieldsBySystemMode,
    getDirtyTabsByFields,
    getInvalidTabsByFields,
    getNewDomainsFromFile,
    isDirtySubCategory,
    isNodeHasProperAz,
    validateAll,
    validateCrossFields
} from '../../common/utils';
import {
    FEATURE_FLAGS_NAMES,
    LAYOUT_TABS,
    ROLES,
    UNASSIGNED_KEY,
    UNSAVED_MESSAGE
} from '@silverfort-nodelibs/common/utils/consts';
import { SYSTEM_MODE_HIDDEN_VIEWS, SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';

import { SUBCATEGORIES } from '../../common/categories';
import { useSaveSettings } from '../../hooks/useSaveSettings';
import {
    APPLICATION_SETTINGS_FIELDS,
    AVAILABILITY_ZONE_FIELDS,
    AZURE_CONNECTORS_FIELDS,
    COMMON_FIELDS,
    COMPANY_INFO_FIELDS,
    CREATED_AT_FIELD,
    DESKTOP_MESSAGING_SERVICES_FIELDS,
    IS_NEW_FIELD,
    LICENSE_FIELDS,
    NODE_FIELDS,
    OKTA_CONNECTORS_FIELDS,
    PROTECTED_DOMAIN_FIELDS
} from '../../common/fields';
import { fieldsReducer, initFieldsState, mapToSettings, STEPS } from './wizardReducer';
import * as availabilityZonesActions from '../AvailabilityZones/availabilityZonesActions';
import * as protectedDomainsActions from '../ProtectedDomains/protectedDomainsActions';
import * as sfNodesActions from '../SilverfortNodes/silverfortNodesActions';
import { SAVE_SETTINGS, VALIDATE } from '../../common/actions';
import { saveSettings as sliceSaveSettings } from '../../containers/settingsSlice';

import {
    AVAILABILITY_ZONES_STEP_ID,
    DESKTOP_APP_STEP_ID,
    FINISH_STEP_ID,
    GENERAL_STEP_ID,
    INTEGRATIONS_STEP_ID,
    LICENSE_STEP_ID,
    MID_FINISH_STEP_ID,
    PROTECTED_DOMAIN_STEP_ID,
    SF_NODES_STEP_ID,
    WELCOME_STEP_ID
} from './wizardIds';

import WizardTabs from './WizardTabs';
import WizardWelcome from '../../components/WizardWelcome/WizardWelcome';
import License from '../../components/License/License';
import RadiusClients from '../../components/RadiusClients/RadiusClients';
import Button from '../../components/Controls/Button/Button';
import CompanyInfo from '../../components/CompanyInfo/CompanyInfo';
import ProtectedDomain from '../../components/ProtectedDomain/ProtectedDomain';
import SilverfortNode from '../../components/SilverfortNode/SilverfortNode';
import WizardSidebar from '../../components/WizardSidebar/WizardSidebar';
import ApplicationSettings from '../../components/ApplicationSettings/ApplicationSettings';
import DesktopMessagingServices from '../../components/DesktopMessagingServices/DesktopMessagingServices';
import ADFSApps from '../../components/FederatedApps/ADFSApps';
import PingFedApps from '../../components/FederatedApps/PingFedApps';
import AzureConnectors from '../../components/Connectors/AzureConnectors';
import NewAzureConnectors from '../../components/Connectors/NewAzureConnectors';
import OktaConnectors from '../../components/Connectors/OktaConnectors';
import ConfirmationMessage from '../../../Controls/ConfirmationMessage';
import ImportFromFile from '../../../../components/Controls/ImportFromFile';
import BulkAssign from '../../../../components/Controls/BulkAssign';

import styles from './Wizard.module.css';

import FinishWizardImg from '../../common/Images/FinishWizardImg.png';
import ManagedWizardTabs from './ManagedWizardTabs';
import AvailabilityZone from '../../components/AvailabilityZone/AvailabilityZone';
import { AZ_ENTITY_TYPES } from '../AvailabilityZones/availabilityZonesReducer';
import useFeature from '../../hooks/featureFlags/useFeature';
import { UPDATE_LICENSE } from '../Licensing/licensingActions';
import { useGetVisibleCategoryIds } from '../../hooks/lockedFeatures/useCategoryDisplay';

const {
    RADIUS_CLIENTS,
    PROTECTED_DOMAINS,
    SILVERFORT_NODES,
    ADFS_FEDERATED_APPS,
    PING_FEDERATED_APPS,
    AVAILABILITY_ZONES
} = SUBCATEGORIES;
const { DOMAIN_ID, DOMAIN_NAME, DOMAIN_SHORT_NAME, API_CERTIFICATE, ROOT_CA_CERTIFICATE } = PROTECTED_DOMAIN_FIELDS;
const { NODE_ID, NODE_NAME, NODE_ENABLED, DOMAINS, DOMAIN } = NODE_FIELDS;
const { LICENSE_KEY } = LICENSE_FIELDS;
const { LICENSE } = SUBCATEGORIES;
const { LDAP_IP_OPTIONS } = COMMON_FIELDS;

const blueButton = {
    color: '#ffffff',
    backgroundColor: '#188ded',
    padding: '4px 16px',
    boxShadow: 'none'
};

const whiteButton = {
    color: '#188ded',
    backgroundColor: '#ffffff',
    padding: '4px 20px',
    boxShadow: 'none',
    border: '1px solid #188ded'
};

const transparentButton = {
    color: '#188ded',
    backgroundColor: '#ffffff',
    padding: '4px 16px',
    boxShadow: 'none'
};

const backButton = {
    margin: '0 20px'
};

const addButtonStyle = {
    width: '120px',
    color: '#ffffff',
    backgroundColor: '#188ded',
    marginLeft: '30px',
    padding: '8px 16px'
};

const arrowButtonStyle = {
    color: '#ffffff',
    backgroundColor: '#188ded',
    padding: '8px 12px',
    outline: 'none'
};

const Wizard = ({ saveFunc, onHide }) => {
    const systemMode = useFeature(FEATURE_FLAGS_NAMES.SystemMode);
    const settings = useSelector((state) => state.settings.settings);
    const user = useSelector((state) => state.userData.user);
    const isEditor = isUserPermitted(user, 'settings', ROLES.EDITOR.value);

    const [step, setStep] = useState(WELCOME_STEP_ID);
    const [tab, setTab] = useState(0);
    const [newDomainIds, setNewDomainIds] = useState(null);
    const [saveError, setSaveError] = useState(null);
    const [saveSuccess, setSaveSuccess] = useState(null);
    const [isDuringSave, setIsDuringSave] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [nextError, setNextError] = useState('');
    const [showUploadDomainsModal, setShowUploadDomainsModal] = useState(false);
    const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
    const [notEdited, setNotEdited] = useState(true);
    const [showMidFinishStep, setShowMidFinishStep] = useState(true);
    const [isDownloadingAdAdapter, setIsDownloadingAdAdapter] = useState(false);
    const [isDownloadingAdConnect, setIsDownloadingAdConnect] = useState(false);
    const [isDownloadAdapterFailed, setIsDownloadAdapterFailed] = useState(false);
    const [isDownloadAdConnectFailed, setIsDownloadAdConnectFailed] = useState(false);
    const [selectNewAzTab, setSelectNewAzTab] = useState(false);

    const [fields, dispatch] = useReducer(fieldsReducer, { settings }, initFieldsState);

    const storeDispatch = useDispatch();

    const ignoredFields = { [SILVERFORT_NODES.ID]: NODE_ENABLED.ID };
    const [save] = useSaveSettings(
        saveFunc,
        settings,
        mapToSettings,
        fields,
        LICENSE_STEP_ID,
        dispatch,
        {},
        ignoredFields,
        true
    );

    const allSteps = useGetVisibleCategoryIds(Object.keys(STEPS));

    const onCancel = useCallback(() => {
        if (settings.License) {
            dispatch({
                type: UPDATE_LICENSE,
                payload: {
                    licenseDetails: settings.License,
                    senderConnectionStrings: settings.RavenSendConnectionStrings,
                    receiverConnectionStrings: settings.RavenReceiveConnectionStrings,
                    systemMode
                }
            });
        }
    }, [settings, dispatch, systemMode]);

    useEffect(() => {
        const categoryContent = document.getElementsByClassName(styles.tabContent);
        if (categoryContent && categoryContent.length > 0) {
            categoryContent[0].scrollTop = 0;
        }
    }, [tab]);

    useEffect(() => {
        setNotEdited(!isDirtySubCategory(fields[AVAILABILITY_ZONES_STEP_ID].fields[AVAILABILITY_ZONES.ID]));
    }, [setNotEdited, fields]);

    useEffect(() => {
        const onUnload = (event) => {
            event.preventDefault();
            event.returnValue = UNSAVED_MESSAGE;
            return UNSAVED_MESSAGE;
        };
        const dirtyTabs = calcDirtyTabs(fields);
        if (dirtyTabs.length > 0) {
            window.addEventListener('beforeunload', onUnload);
        } else {
            window.removeEventListener('beforeunload', onUnload);
        }
        return () => {
            window.removeEventListener('beforeunload', onUnload);
        };
    }, [fields]);

    useEffect(() => {
        setNewDomainIds(
            fields[PROTECTED_DOMAIN_STEP_ID].fields[PROTECTED_DOMAINS.ID].value
                .filter(
                    (domain) => !Object.keys(settings.DomainsConfigurations || {}).includes(domain[DOMAIN_ID.ID].value)
                )
                .map((domain) => domain[DOMAIN_ID.ID].value)
        );
    }, [fields, settings]);

    useEffect(() => {
        if (selectNewAzTab) {
            const visibleFields = fields[AVAILABILITY_ZONES_STEP_ID].fields[AVAILABILITY_ZONES.ID].value;
            const lastTabId = Object.keys(visibleFields).reduce((prev, current) => {
                if (!visibleFields[prev].fields[CREATED_AT_FIELD.ID]) {
                    return current;
                } else if (!visibleFields[current].fields[CREATED_AT_FIELD.ID]) {
                    return prev;
                }
                return visibleFields[prev].fields[CREATED_AT_FIELD.ID].value >
                    visibleFields[current].fields[CREATED_AT_FIELD.ID].value
                    ? prev
                    : current;
            });
            setTab(Number(lastTabId));
            setSelectNewAzTab(false);
        }
    }, [selectNewAzTab, fields]);

    const getNodesConfiguration = useCallback(async () => {
        setIsLoadingData(true);

        try {
            const results = await httpGet('/Settings/getNodesConfiguration');

            if (results?.data?.nodesConfigurations) {
                storeDispatch(sliceSaveSettings({ NodesConfigurations: results.data.nodesConfigurations }));
                dispatch({
                    type: sfNodesActions.UPDATE_NODES_CONFIGURATION,
                    payload: { nodesConfiguration: results.data.nodesConfigurations }
                });
            }
        } catch (err) {
            console.error(err);
            setSaveError(err.response?.data?.error || 'Error occurred!');
            setTimeout(() => setSaveError(''), 3000);
        } finally {
            setTimeout(() => setIsLoadingData(false), 2000);
        }
    }, [storeDispatch]);

    const getAzConfiguration = useCallback(async () => {
        setIsLoadingData(true);
        try {
            const results = await httpGet('/Settings/GetAzSettings');
            if (results?.data?.AzConfigExtraData && results?.data?.azConfig) {
                storeDispatch(
                    sliceSaveSettings({
                        AzConfigExtraData: results.data.AzConfigExtraData,
                        azConfig: results.data.azConfig
                    })
                );
                dispatch({
                    type: availabilityZonesActions.UPDATE_CONFIG,
                    payload: { azConfig: results.data.azConfig, AzConfigExtraData: results.data.AzConfigExtraData }
                });
            }
        } catch (err) {
            console.error(err);
            setSaveError(err.response?.data?.error || 'Error occurred!');
            setTimeout(() => setSaveError(''), 3000);
        } finally {
            setTimeout(() => setIsLoadingData(false), 2000);
        }
    }, [storeDispatch]);

    const nextStep = useCallback(() => {
        setIsLoadingData(false);
        const validatedFields = validateAll(fields, ignoredFields);
        const invalidTabs = calcInvalidTabs(validatedFields);
        if (invalidTabs.length > 0 && invalidTabs.includes(step)) {
            dispatch({ type: VALIDATE, payload: { saveAll: false, selectedTab: step, ignoredFields } });
            setNextError('Validation Failed!');
            setTimeout(() => setNextError(''), 5000);
            return;
        }
        const validatedCrossFields = validateCrossFields(validatedFields, step);
        const invalidCrossTabs = calcInvalidTabs(validatedCrossFields);
        if (invalidCrossTabs.length > 0 && invalidCrossTabs.includes(step)) {
            dispatch({ type: VALIDATE, payload: { saveAll: false, selectedTab: step, ignoredFields } });
            setNextError('Validation Failed!');
            setTimeout(() => setNextError(''), 5000);
            return;
        }

        const currIndex = allSteps.findIndex((s) => s === step);
        const showMidFinishStepIndex = allSteps.findIndex((s) => s === MID_FINISH_STEP_ID);
        let nextIndex = currIndex + 1;

        if (!showMidFinishStep && nextIndex === showMidFinishStepIndex) {
            nextIndex = currIndex + 2;
        }
        if (allSteps[nextIndex]) {
            setStep(allSteps[nextIndex]);
            setTab(0);
            if (allSteps[nextIndex] === SF_NODES_STEP_ID) {
                getNodesConfiguration();
            }
            if (allSteps[nextIndex] === AVAILABILITY_ZONES_STEP_ID) {
                getAzConfiguration();
            }
        }
    }, [step, setStep, fields, ignoredFields, getNodesConfiguration, showMidFinishStep, getAzConfiguration]);

    const prevStep = useCallback(() => {
        setIsLoadingData(false);
        const currIndex = allSteps.findIndex((s) => s === step);
        const showMidFinishStepIndex = allSteps.findIndex((s) => s === MID_FINISH_STEP_ID);
        const prevIndex = currIndex - 1;

        if (!showMidFinishStep && prevIndex === showMidFinishStepIndex) {
            if (currIndex > 1) {
                setStep(allSteps[prevIndex - 1]);
                setTab(0);
            }
        } else if (currIndex > 0) {
            setStep(allSteps[prevIndex]);
            setTab(0);
        }
    }, [step, setStep, showMidFinishStep]);

    const closeWizard = () => {
        const dirtyTabs = calcDirtyTabs(fields);
        if (dirtyTabs.length === 0) {
            return onHide();
        }
        // eslint-disable-next-line no-restricted-globals
        const result = confirm(UNSAVED_MESSAGE);
        if (result === true) {
            return onHide();
        }
    };

    const saveSettings = async (closeWizard = true) => {
        setIsDuringSave(true);
        try {
            await save(true);
            if (closeWizard) {
                return onHide();
            }
            setSaveSuccess('Saved!');
            setTimeout(() => setSaveSuccess(''), 3000);
        } catch (err) {
            console.error(err);
            const errorMessage = err.response?.data?.error || 'Error occurred while saving settings!';
            setSaveError(errorMessage);
            setTimeout(() => setSaveError(''), 3000);
        } finally {
            if (!closeWizard) {
                setTimeout(() => setIsDuringSave(false), 3000);
            }
        }
    };

    const onBulkAssignClicked = useCallback(() => {
        setShowBulkAssignModal(true);
    }, []);

    const addDomain = useCallback(() => {
        dispatch({
            type: protectedDomainsActions.ADD_DOMAIN,
            payload: { systemMode }
        });
        setTab(0);
    }, [systemMode]);

    const addAz = useCallback(() => {
        dispatch({
            type: availabilityZonesActions.ADD_AZ
        });
        setSelectNewAzTab(true);
    }, []);

    const discardNode = useCallback(
        (nodeId) => {
            dispatch({
                type: sfNodesActions.DISCARD_NODE,
                payload: { nodeId, settings, systemMode }
            });
        },
        [dispatch, settings, systemMode]
    );

    const saveLicense = async () => {
        const validatedFields = validateAll(fields, ignoredFields);
        const invalidTabs = calcInvalidTabs(validatedFields);
        if (invalidTabs.length > 0 && invalidTabs.includes(step)) {
            dispatch({ type: VALIDATE, payload: { saveAll: false, selectedTab: step, ignoredFields } });
            setNextError('Validation Failed!');
            setTimeout(() => setNextError(''), 5000);
            return;
        }

        setIsDuringSave(true);
        let licenseSaved = true;
        try {
            const licenseSettings = {
                License: fields[LICENSE_STEP_ID].fields[LICENSE_FIELDS.LICENSE_KEY.ID].licenseDetails
            };
            await httpPost('/Settings/saveLicense', licenseSettings);

            storeDispatch(sliceSaveSettings(licenseSettings));
            dispatch({ type: SAVE_SETTINGS, payload: { saveAll: false, selectedTab: LICENSE_STEP_ID } });
            nextStep();
        } catch (err) {
            licenseSaved = false;
            console.error(err);
            if (err.response?.data?.error) {
                setSaveError(err.response.data.error);
                setTimeout(() => setSaveError(''), 3000);
            } else {
                setSaveError('Error occurred!');
                setTimeout(() => setSaveError(''), 3000);
            }
        } finally {
            setTimeout(() => setIsDuringSave(false), licenseSaved ? 0 : 3000);
        }
    };

    const uploadFromFile = useCallback(
        (fileContent) => {
            const result = getNewDomainsFromFile(fileContent, settings.DomainsConfigurations);
            if (result.error) {
                return result.error;
            }

            for (const option of settings.LdapServerOptions) {
                if (result.ldapServers.has(option.value)) {
                    result.ldapServers.delete(option.value);
                }
            }

            dispatch({
                type: protectedDomainsActions.UPDATE_DOMAINS_FROM_FILE,
                payload: {
                    systemMode,
                    newDomains: result.newDomains,
                    LdapServerOptions: Array.from(result.ldapServers).map((option) => {
                        return { value: option, label: option };
                    })
                }
            });

            setShowUploadDomainsModal(false);
            return [];
        },
        [settings.DomainsConfigurations, settings.LdapServerOptions]
    );

    const continueFromMidFinish = useCallback(() => {
        nextStep();
        setShowMidFinishStep(false);
    }, [nextStep]);

    const downloadCastleInstaller = useCallback(async () => {
        const wizardDomainsConfigurations = {};
        for (const domainFields of fields[PROTECTED_DOMAIN_STEP_ID].fields[PROTECTED_DOMAINS.ID].value) {
            const domainId = domainFields[DOMAIN_ID.ID].value;
            const newDomainObj = {
                DomainName: domainFields[DOMAIN_NAME.ID].value,
                CertificateData: domainFields[API_CERTIFICATE.ID].value?.data || ''
            };
            wizardDomainsConfigurations[domainId] = newDomainObj;
        }
        setIsDownloadingAdAdapter(true);
        try {
            await httpPost('/Settings/generateCastleInstaller', {
                DomainsConfigurations: JSON.stringify(wizardDomainsConfigurations),
                clientHostname: window.location.hostname
            });
            downloadFile('/Settings/downloadPreGeneratedCastleInstaller');
            setIsDownloadAdapterFailed(false);
        } catch (err) {
            setIsDownloadAdapterFailed(true);
            console.error('an error occured while trying to download AD Adapter Installer.', err);
        } finally {
            setIsDownloadingAdAdapter(false);
        }
    }, [fields]);

    const downloadAdConnectInstaller = useCallback(async () => {
        setIsDownloadingAdConnect(true);
        try {
            await httpPost('/Settings/generateAdConnectInstaller');
            downloadFile('/Settings/downloadPreGeneratedAdConnectInstaller');
            setIsDownloadAdConnectFailed(false);
        } catch (err) {
            setIsDownloadAdConnectFailed(true);
            console.error('an error occured while trying to download AD Connect Installer.', err);
        } finally {
            setIsDownloadingAdConnect(false);
        }
    }, [fields]);

    const licenseExists = fields[LICENSE_STEP_ID]?.fields[LICENSE_FIELDS.LICENSE_KEY.ID].value;

    const shouldRequireLicenseForNext = systemMode === SYSTEM_MODES.ON_PREM && !licenseExists;

    let content = null;
    if (step === WELCOME_STEP_ID) {
        content = (
            <div className={styles.tabContent}>
                <WizardWelcome />
            </div>
        );
    } else if (step === LICENSE_STEP_ID) {
        if (
            settings.License?.licenseData &&
            !fields[LICENSE.ID].fields[LICENSE_KEY.ID].isDirty &&
            JSON.stringify(fields[LICENSE.ID].fields[LICENSE_KEY.ID].licenseDetails) !==
                JSON.stringify(settings.License)
        ) {
            dispatch({
                type: UPDATE_LICENSE,
                payload: {
                    licenseDetails: settings.License,
                    senderConnectionStrings: settings.RavenSendConnectionStrings,
                    receiverConnectionStrings: settings.RavenReceiveConnectionStrings,
                    systemMode
                }
            });
        }

        content = (
            <div id="settings-wizard-license-tab" className={`${styles.tabContent} py-4`}>
                <License
                    fields={fields[LICENSE_STEP_ID].fields}
                    dispatch={dispatch}
                    licenseExtraData={settings.licenseExtraData}
                    onCancel={onCancel}
                    save={save}
                    minified={true}
                />
            </div>
        );
    } else if (step === GENERAL_STEP_ID) {
        const companyInfoFieldsIds = Object.values(COMPANY_INFO_FIELDS).map((field) => field.ID);
        const fieldsToTabsMap = {
            0: Object.values(fields[GENERAL_STEP_ID].fields).filter((field) => companyInfoFieldsIds.includes(field.id)),
            1: [fields[GENERAL_STEP_ID].fields[RADIUS_CLIENTS.ID]]
        };
        const invalidTabs = getInvalidTabsByFields(fieldsToTabsMap);
        const dirtyTabs = getDirtyTabsByFields(fieldsToTabsMap);
        const buildGeneralTabsList = () => {
            const tabs = [
                {
                    header: 'Company Info',
                    component: (
                        <div id="settings-wizard-general-company-info-tab">
                            <CompanyInfo fields={fields[GENERAL_STEP_ID].fields} dispatch={dispatch} />
                        </div>
                    )
                }
            ];

            if (!SYSTEM_MODE_HIDDEN_VIEWS[LAYOUT_TABS.SYSTEM_SETUP.ID][systemMode].tabs.has('radiusClients')) {
                tabs.push({
                    header: 'RADIUS Clients',
                    component: (
                        <div id="settings-wizard-general-radius-tab">
                            <RadiusClients fields={fields[GENERAL_STEP_ID].fields} dispatch={dispatch} />
                        </div>
                    )
                });
            }

            return tabs;
        };

        content = (
            <ManagedWizardTabs
                tabsList={buildGeneralTabsList()}
                invalidTabs={invalidTabs}
                tabsContainerClass={styles.tabContent}
                dirtyTabs={dirtyTabs}
                key={GENERAL_STEP_ID}
            />
        );
    } else if (step === PROTECTED_DOMAIN_STEP_ID) {
        const fieldsToTabsMap = {};
        const availableTabs = [];
        fields[PROTECTED_DOMAIN_STEP_ID].fields[PROTECTED_DOMAINS.ID].value.forEach((domain, index) => {
            availableTabs.push(domain[DOMAIN_NAME.ID].value);
            fieldsToTabsMap[index] = Object.values(domain);
        });
        const invalidTabs = getInvalidTabsByFields(fieldsToTabsMap);
        const dirtyTabs = getDirtyTabsByFields(fieldsToTabsMap);

        content = (
            <WizardTabs
                availableTabs={availableTabs}
                selectedTab={tab}
                setSelectedTab={setTab}
                tabButton={
                    <ButtonGroup
                        variant="contained"
                        color="primary"
                        aria-label="split button"
                        style={{ marginTop: '3px', display: 'flex', justifyContent: 'flex-end' }}>
                        {newDomainIds.length > 0 ? (
                            <Tooltip
                                className={styles.tabTooltip}
                                enterDelay={100}
                                title={<div>{'Click Save to add new domain'}</div>}
                                placement={'bottom'}
                                classes={{ tooltip: 'moreInfoIcon-tooltip' }}>
                                <span>
                                    <ReactButton style={{ ...addButtonStyle, pointerEvents: 'none' }} disabled>
                                        + Add Domain
                                    </ReactButton>
                                </span>
                            </Tooltip>
                        ) : (
                            <ReactButton
                                id="settings-wizard-protected-domains-add-domain"
                                style={addButtonStyle}
                                onClick={addDomain}>
                                + Add Domain
                            </ReactButton>
                        )}
                        <DropdownButton id={'saveDropdown'} pullRight style={arrowButtonStyle} title={''}>
                            <MenuItem
                                id="settings-wizard-protected-domains-upload-from-file"
                                onClick={() => setShowUploadDomainsModal(true)}>
                                Upload from file
                            </MenuItem>
                        </DropdownButton>
                    </ButtonGroup>
                }
                invalidTabs={invalidTabs}
                dirtyTabs={dirtyTabs}
            />
        );

        const domain = fields[PROTECTED_DOMAIN_STEP_ID].fields[PROTECTED_DOMAINS.ID].value[tab];
        if (domain) {
            const domainId = domain[DOMAIN_ID.ID].value;
            const domainNodes = {};

            for (const nodeId in settings.NodesConfigurations || {}) {
                const nodeConfig = settings.NodesConfigurations[nodeId];
                if (nodeConfig && nodeConfig.domainsConfiguration && domainId in nodeConfig.domainsConfiguration) {
                    domainNodes[nodeId] = nodeConfig;
                }
            }

            const domainFields = filterFieldsBySystemMode(
                fields[PROTECTED_DOMAIN_STEP_ID].fields[PROTECTED_DOMAINS.ID].value[tab],
                systemMode
            );

            content = (
                <>
                    {content}
                    <div id="settings-wizard-protected-domains-content" className={styles.tabContent}>
                        <ProtectedDomain
                            key={domainId}
                            domainId={domainId}
                            fields={domainFields}
                            dispatch={dispatch}
                            ldapServerOptions={fields[PROTECTED_DOMAIN_STEP_ID].fields[LDAP_IP_OPTIONS.ID].value}
                            domainNodes={domainNodes}
                            isNewDomain={newDomainIds.includes(domainId)}
                            onDelete={() => setTab(0)}
                            allowDelete={isEditor && newDomainIds.includes(domainId)}
                        />
                    </div>
                </>
            );
        }
    } else if (step === SF_NODES_STEP_ID) {
        const fieldsToTabsMap = {};
        const availableTabs = [];
        fields[SF_NODES_STEP_ID].fields[SILVERFORT_NODES.ID].value.forEach((node, index) => {
            availableTabs.push(node[NODE_NAME.ID].value);
            fieldsToTabsMap[index] = Object.values(node);
        });
        const invalidTabs = getInvalidTabsByFields(fieldsToTabsMap);
        const dirtyTabs = getDirtyTabsByFields(fieldsToTabsMap);
        content = (
            <WizardTabs
                availableTabs={availableTabs}
                selectedTab={tab}
                setSelectedTab={setTab}
                invalidTabs={invalidTabs}
                dirtyTabs={dirtyTabs}
            />
        );

        const node = fields[SF_NODES_STEP_ID].fields[SILVERFORT_NODES.ID].value[tab];
        if (node) {
            const nodeId = node[NODE_ID.ID].value;
            const domainsOptions = {};

            Object.values(settings.DomainsConfigurations || {}).forEach((domain) => {
                domainsOptions[domain.id] = {
                    value: domain.id,
                    label: domain.DomainName,
                    serviceAccountUser: domain.ServiceAccountUsername,
                    isCert: !!domain.rootCACertificateData?.data
                };
            });

            for (const domainFields of fields[PROTECTED_DOMAIN_STEP_ID].fields[PROTECTED_DOMAINS.ID].value) {
                const domainId = domainFields[DOMAIN_ID.ID].value;
                if (!domainsOptions[domainId]) {
                    domainsOptions[domainId] = {
                        value: domainId,
                        label: domainFields[DOMAIN_NAME.ID].value,
                        serviceAccountUser: '',
                        isCert: !!domainFields[ROOT_CA_CERTIFICATE.ID].value?.data
                    };
                } else {
                    domainsOptions[domainId].label = domainFields[DOMAIN_NAME.ID].value;
                }
                const domainName = domainFields[DOMAIN_NAME.ID].value;
                const domainShortName = domainFields[DOMAIN_SHORT_NAME.ID].value;
                const domainParts = domainName.split('.');
                const baseDNParts = [];
                if (domainParts && domainParts.length > 0) {
                    for (const part of domainParts) {
                        baseDNParts.push('DC=' + part);
                    }
                }
                const domainData = {
                    DomainBaseDN: baseDNParts.join(','),
                    DomainName: domainName,
                    DomainShortName: domainShortName
                };
                domainsOptions[domainId].domainData = domainData;
            }

            for (const nodeFields of fields[SF_NODES_STEP_ID].fields[SILVERFORT_NODES.ID].value) {
                for (const nodeDomainFields of nodeFields[NODE_FIELDS.DOMAINS.ID].value) {
                    const domainId = nodeDomainFields[NODE_FIELDS.DOMAIN.ID].value;
                    if (domainId && domainsOptions[domainId] && !domainsOptions[domainId].serviceAccountUser) {
                        const nodeDomainServiceAccount =
                            nodeDomainFields[PROTECTED_DOMAIN_FIELDS.DOMAIN_SERVICE_ACCOUNT.ID].value;
                        if (nodeDomainServiceAccount && nodeDomainServiceAccount.user) {
                            const newDomainOption = { ...domainsOptions[domainId] };
                            newDomainOption.serviceAccountUser = nodeDomainServiceAccount.user;
                            domainsOptions[domainId] = newDomainOption;
                        }
                    }
                }
            }
            const nodeTab = fields[SF_NODES_STEP_ID].fields[SILVERFORT_NODES.ID].value[tab];
            const tabDisabled = nodeTab[NODE_ENABLED.ID] && nodeTab[NODE_ENABLED.ID].value === false;
            const nodeConfiguration = settings.NodesConfigurations?.[nodeId];
            const isProperAz =
                nodeConfiguration && settings.azConfig
                    ? isNodeHasProperAz(nodeConfiguration, settings.azConfig)
                    : false;
            content = (
                <>
                    {content}
                    <div
                        id="settings-wizard-silverfort—nodes-content"
                        className={`${tabDisabled ? styles.disabledTabContent : styles.tabContent} `}>
                        <SilverfortNode
                            nodeId={nodeId}
                            nodeIp={nodeConfiguration?.ipAddress || ''}
                            isProperAz={isProperAz}
                            nodeQueueId={nodeConfiguration?.WallQueueId || ''}
                            fields={fields[SF_NODES_STEP_ID].fields[SILVERFORT_NODES.ID].value[tab]}
                            dispatch={dispatch}
                            domainsOptions={domainsOptions}
                            ldapServerOptions={fields[SF_NODES_STEP_ID].fields[LDAP_IP_OPTIONS.ID].value}
                            discardNode={() => discardNode(nodeId)}
                            allowDelete={false}
                            disableAdSyncOption={
                                !fields[SF_NODES_STEP_ID].fields[SILVERFORT_NODES.ID].value[tab][NODE_FIELDS.DOMAINS.ID]
                                    .value.length
                            }
                            isFromWizard={true}
                        />
                    </div>
                </>
            );
        } else {
            content = <div className={styles.noNodes}>No nodes to configure</div>;
        }
    } else if (step === AVAILABILITY_ZONES_STEP_ID) {
        const visibleFields = fields[AVAILABILITY_ZONES_STEP_ID].fields[AVAILABILITY_ZONES.ID].value;
        const { shared } = fields[AVAILABILITY_ZONES_STEP_ID].fields;
        const fieldsToTabsMap = {};
        const availableTabs = [];
        const indicesToGuids = {};
        const newTabsIndices = [];
        const assignedWalls = [];

        visibleFields.forEach((az, index) => {
            const azData = visibleFields[index].fields;
            assignedWalls.push(...azData[AVAILABILITY_ZONE_FIELDS.SF_NODES.ID].value);
            availableTabs.push(azData[AVAILABILITY_ZONE_FIELDS.NAME.ID].value);
            fieldsToTabsMap[index] = Object.values(azData);
            indicesToGuids[index] = visibleFields[index].id;
            if (azData[IS_NEW_FIELD.ID].value) {
                newTabsIndices.push(index);
            }
        });

        const invalidTabs = getInvalidTabsByFields(fieldsToTabsMap);
        const dirtyTabs = [
            ...getDirtyTabsByFields(fieldsToTabsMap),
            ...newTabsIndices.map((index) => index.toString())
        ];

        content = (
            <WizardTabs
                availableTabs={availableTabs}
                selectedTab={tab}
                setSelectedTab={setTab}
                tabButton={
                    <ButtonGroup
                        variant="contained"
                        color="primary"
                        aria-label="split button"
                        style={{ marginTop: '3px', display: 'flex', justifyContent: 'flex-end' }}>
                        <ReactButton
                            id="settings-wizard-availability-zones-add-availability-zone"
                            style={{ ...addButtonStyle, width: '180px' }}
                            onClick={addAz}>
                            + Add Availability Zone
                        </ReactButton>
                    </ButtonGroup>
                }
                invalidTabs={invalidTabs}
                dirtyTabs={dirtyTabs}
            />
        );

        const domainOptions = [];
        const domainGuids = [];
        for (const domainFields of fields[PROTECTED_DOMAIN_STEP_ID].fields[PROTECTED_DOMAINS.ID].value) {
            domainOptions.push({
                label: domainFields[DOMAIN_NAME.ID].value,
                value: domainFields[DOMAIN_ID.ID].value
            });
            domainGuids.push(domainFields[DOMAIN_ID.ID].value);
        }

        const unassignedWalls = [];
        const wallsWithDomains = [];
        for (const node of fields[SF_NODES_STEP_ID].fields[SILVERFORT_NODES.ID].value) {
            const nodeId = node[NODE_ID.ID].value;
            const nodeDomains = node[NODE_FIELDS.DOMAINS.ID].value;
            const nodeName = node[NODE_FIELDS.NODE_NAME.ID].value;
            if (!assignedWalls.includes(nodeId)) {
                unassignedWalls.push(nodeId);
            }
            const nodeDomainsIds = [];
            for (const domain of nodeDomains) {
                const domainId = domain[NODE_FIELDS.DOMAIN.ID].value;
                if (domainId && domainGuids.includes(domainId)) {
                    nodeDomainsIds.push(domainId);
                }
            }
            wallsWithDomains[nodeId] = {
                name: nodeName,
                id: nodeId,
                domains: nodeDomainsIds
            };
        }

        const sharedWithWizardUpdates = {
            ...shared,
            domainOptions,
            wallsWithDomains,
            [UNASSIGNED_KEY]: {
                ...shared[UNASSIGNED_KEY],
                walls: unassignedWalls
            }
        };

        const azGuid = indicesToGuids[tab];
        if (azGuid) {
            content = (
                <>
                    {content}
                    <div id="settings-wizard-availability-zones-content" className={styles.tabContent}>
                        <AvailabilityZone
                            azGuid={azGuid}
                            fields={visibleFields[tab].fields}
                            dispatch={dispatch}
                            shared={sharedWithWizardUpdates}
                            rerenderOnChange={showBulkAssignModal}
                            hideDcs={true}
                            dcsValue={visibleFields[tab].fields[AVAILABILITY_ZONE_FIELDS.DCS.ID].value}
                            protectedDomainsValue={
                                visibleFields[tab].fields[AVAILABILITY_ZONE_FIELDS.PROTECTED_DOMAINS.ID].value
                            }
                            sfNodesValue={visibleFields[tab].fields[AVAILABILITY_ZONE_FIELDS.SF_NODES.ID].value}
                            allowDelete={isEditor}
                            setTabToZero={() => setTab(0)}
                            disableDelete={!notEdited}
                        />
                    </div>
                </>
            );
        }
    } else if (step === DESKTOP_APP_STEP_ID) {
        const applicationSettingsFieldsIds = Object.values(APPLICATION_SETTINGS_FIELDS).map((field) => field.ID);
        const fieldsToTabsMap = {
            0: Object.values(fields[DESKTOP_APP_STEP_ID].fields).filter((field) =>
                applicationSettingsFieldsIds.includes(field.id)
            ),
            1: [fields[DESKTOP_APP_STEP_ID].fields[DESKTOP_MESSAGING_SERVICES_FIELDS.MESSAGING_SERVICES.ID]]
        };
        const invalidTabs = getInvalidTabsByFields(fieldsToTabsMap);
        const dirtyTabs = getDirtyTabsByFields(fieldsToTabsMap);

        content = (
            <ManagedWizardTabs
                tabsList={[
                    {
                        header: 'Application Settings',
                        component: (
                            <div id="settings-wizard-dektop-application-settings">
                                <ApplicationSettings fields={fields[DESKTOP_APP_STEP_ID].fields} dispatch={dispatch} />
                            </div>
                        )
                    },
                    {
                        header: 'Desktop Messaging Services',
                        component: (
                            <div id="settings-wizard-dektop-application-messaging-services">
                                <DesktopMessagingServices
                                    fields={fields[DESKTOP_APP_STEP_ID].fields}
                                    dispatch={dispatch}
                                />
                            </div>
                        )
                    }
                ]}
                invalidTabs={invalidTabs}
                tabsContainerClass={styles.tabContent}
                dirtyTabs={dirtyTabs}
                key={DESKTOP_APP_STEP_ID}
            />
        );
    } else if (step === INTEGRATIONS_STEP_ID) {
        const azureConnectorsFieldsIds = Object.values(AZURE_CONNECTORS_FIELDS).map((field) => field.ID);
        const oktaConnectorsFieldsIds = Object.values(OKTA_CONNECTORS_FIELDS).map((field) => field.ID);

        const fieldsToTabsMap = {
            0: [fields[INTEGRATIONS_STEP_ID].fields[ADFS_FEDERATED_APPS.ID]],
            1: [fields[INTEGRATIONS_STEP_ID].fields[PING_FEDERATED_APPS.ID]],
            2: Object.values(fields[INTEGRATIONS_STEP_ID].fields).filter((field) =>
                azureConnectorsFieldsIds.includes(field.id)
            ),
            3: Object.values(fields[INTEGRATIONS_STEP_ID].fields).filter((field) =>
                oktaConnectorsFieldsIds.includes(field.id)
            )
        };
        const invalidTabs = getInvalidTabsByFields(fieldsToTabsMap);
        const dirtyTabs = getDirtyTabsByFields(fieldsToTabsMap);

        let azureConnectorsComponent = null;
        if (Object.keys(fields[INTEGRATIONS_STEP_ID].fields).includes(AZURE_CONNECTORS_FIELDS.AZURE_AD_CONNECTOR.ID)) {
            azureConnectorsComponent = (
                <AzureConnectors
                    id="settings-wizard-integrations-azure ad"
                    fields={fields[INTEGRATIONS_STEP_ID].fields}
                    dispatch={dispatch}
                    authorizePermission={isUserPermitted(user, 'authorize', ROLES.EDITOR.value)}
                />
            );
        } else {
            azureConnectorsComponent = (
                <NewAzureConnectors
                    id="settings-wizard-integrations-new azure ad"
                    fields={fields[INTEGRATIONS_STEP_ID].fields}
                    dispatch={dispatch}
                />
            );
        }

        content = (
            <ManagedWizardTabs
                tabsList={[
                    {
                        header: 'ADFS Apps',
                        component: (
                            <div id="settings-wizard-integrations-adfs apps">
                                <ADFSApps fields={fields[INTEGRATIONS_STEP_ID].fields} dispatch={dispatch} />
                            </div>
                        )
                    },
                    {
                        header: 'PingFederate Apps',
                        component: (
                            <div id="settings-wizard-integrations-pingfederate apps">
                                <PingFedApps fields={fields[INTEGRATIONS_STEP_ID].fields} dispatch={dispatch} />
                            </div>
                        )
                    },
                    {
                        header: 'Entra ID',
                        component: azureConnectorsComponent
                    },
                    {
                        header: 'Okta Connectors',
                        component: (
                            <div id="settings-wizard-integrations-octa connectors">
                                <OktaConnectors
                                    fields={fields[INTEGRATIONS_STEP_ID].fields}
                                    dispatch={dispatch}
                                    authorizePermission={isUserPermitted(user, 'authorize', ROLES.EDITOR.value)}
                                />
                            </div>
                        )
                    }
                ]}
                invalidTabs={invalidTabs}
                tabsContainerClass={styles.tabContent}
                dirtyTabs={dirtyTabs}
                key={INTEGRATIONS_STEP_ID}
            />
        );
    } else if (step === FINISH_STEP_ID) {
        content = (
            <div id="settings-wizard-finish-page" className={styles.finishContent}>
                <div className={styles.finishText}>Configuration Completed</div>
                <img className={styles.finishImg} src={FinishWizardImg} alt="" />
            </div>
        );
    } else if (step === MID_FINISH_STEP_ID) {
        content = (
            <div className={styles.finishContent}>
                <div className={styles.mainFinishContent}>
                    <div className={styles.finishText}>Configuration Completed</div>
                    <img className={styles.finishImg} src={FinishWizardImg} alt="" />
                    <div className={styles.downloadAdapter}>
                        {systemMode !== SYSTEM_MODES.ON_PREM && (
                            <Button
                                id="settings-wizard-finish-page-download-adapter-btn"
                                style={{ ...blueButton, display: 'block', marginBottom: '10px' }}
                                disabled={isDuringSave || isDownloadingAdConnect}
                                onClick={downloadAdConnectInstaller}>
                                <i className="fa fa-download" />
                                Download AD Connect
                            </Button>
                        )}
                        <Button
                            id="settings-wizard-finish-page-download-adapter-btn"
                            style={blueButton}
                            disabled={isDuringSave || isDownloadingAdAdapter}
                            onClick={downloadCastleInstaller}>
                            <i className="fa fa-download" />
                            Download AD Adapter
                        </Button>
                        <div className={`${styles.loadingContent}`}>
                            {(isDownloadingAdAdapter || isDownloadingAdConnect) && (
                                <>
                                    <i className="fa fa-spinner fa-pulse fa-fw"></i>
                                    {'Loading...'}
                                </>
                            )}
                            {isDownloadAdapterFailed && !isDownloadingAdAdapter && (
                                <div className={styles.errorMsg}>Failed to download the Ad Adapter!</div>
                            )}
                            {isDownloadAdConnectFailed && !isDownloadingAdConnect && (
                                <div className={styles.errorMsg}>Failed to download Ad Conenct!</div>
                            )}
                        </div>
                    </div>
                </div>
                {systemMode === SYSTEM_MODES.ON_PREM && (
                    <div className={styles.continueConfiguring}>
                        <div className={styles.continueText}>Do you want to continue to optional configurations?</div>
                        <Button
                            id="settings-wizard-finish-page-continue-btn"
                            style={blueButton}
                            disabled={isDuringSave}
                            onClick={continueFromMidFinish}>
                            Continue
                        </Button>
                    </div>
                )}
            </div>
        );
    }

    const isFirstStep = step === WELCOME_STEP_ID;
    const isLastStep = step === FINISH_STEP_ID || step === MID_FINISH_STEP_ID;
    const isLicenseStepAndDirty = step === LICENSE_STEP_ID && fields[LICENSE_STEP_ID].fields[LICENSE_KEY.ID].isDirty;
    return (
        <Modal
            id="wizard-modal"
            dialogClassName="modal-lg"
            show
            backdrop="static"
            className="z-[800]"
            backdropClassName={'z-[800]'}>
            <Modal.Header>
                <div className={styles.headerContainer}>
                    <div id="settings-wizard-wizzard-title" className={styles.header}>
                        Configuration Wizard
                    </div>
                    <div id="settings-wizard-close-btn" className={styles.remove} onClick={closeWizard}></div>
                </div>
            </Modal.Header>
            <Modal.Body style={{ padding: 0 }}>
                <div className={styles.body}>
                    <WizardSidebar steps={allSteps} selectedStep={step} showMidFinishStep={showMidFinishStep} />
                    {!isLoadingData && <div className={styles.wizardContent}>{content}</div>}
                    {isLoadingData && (
                        <div className={`${styles.wizardContent} ${styles.loadingContent}`}>
                            <i className="fa fa-spinner fa-pulse fa-fw"></i>
                            {'Loading...'}
                        </div>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <div className={styles.footer}>
                    <div className={styles.buttonsLeft}>
                        {step !== WELCOME_STEP_ID && (
                            <Button
                                id="settings-wizard-save-btn"
                                style={whiteButton}
                                disabled={isDuringSave}
                                onClick={() => saveSettings(false)}>
                                Save
                            </Button>
                        )}
                        {saveError && <div className={styles.errorMsg}>{saveError}</div>}
                        {saveSuccess && <div className={styles.successMsg}>{saveSuccess}</div>}
                        {step === AVAILABILITY_ZONES_STEP_ID && (
                            <Button
                                style={{ ...whiteButton, marginLeft: '20px' }}
                                onClick={() => onBulkAssignClicked()}
                                disabled={
                                    fields[AVAILABILITY_ZONES_STEP_ID].fields[AVAILABILITY_ZONES.ID].value.length < 1 ||
                                    isLoadingData
                                }>
                                Bulk Assign
                            </Button>
                        )}
                    </div>
                    <div className={styles.notes}>
                        {isLastStep && (
                            <div className={styles.adSyncWarning}>
                                By clicking FINISH, AD Sync will begin automatically.
                            </div>
                        )}
                        {nextError && <div className={styles.errorMsg}>{nextError}</div>}
                    </div>
                    <div className={styles.buttonsRight}>
                        <div className={styles.buttons}>
                            <Button
                                id="settings-wizard-back-btn"
                                style={{ ...(isFirstStep ? transparentButton : blueButton), ...backButton }}
                                disabled={isFirstStep}
                                onClick={prevStep}>
                                <div className={styles.buttonContent}>
                                    <div>{'<'}</div>
                                    <div>Back</div>
                                </div>
                            </Button>
                            <Button
                                id="settings-wizard-next-finish-btn"
                                style={isLastStep ? whiteButton : blueButton}
                                disabled={isDuringSave || (step === LICENSE_STEP_ID && shouldRequireLicenseForNext)}
                                onClick={isLastStep ? saveSettings : isLicenseStepAndDirty ? saveLicense : nextStep}>
                                <div>
                                    {isLastStep && <div>Finish</div>}
                                    {isLicenseStepAndDirty && <div>{'Activate & Next >'}</div>}
                                    {!isLastStep && !isLicenseStepAndDirty && (
                                        <div className={styles.buttonContent}>
                                            <div>Next</div>
                                            <div>{'>'}</div>
                                        </div>
                                    )}
                                </div>
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal.Footer>
            {showUploadDomainsModal && (
                <ImportFromFile
                    cancelClicked={() => {
                        setShowUploadDomainsModal(false);
                    }}
                    importClicked={uploadFromFile}
                    modalTitle={'domains from file'}
                    importDescName={'domains'}
                    maxFileSize={10000000}
                    maxFileSizeText={'10 MB'}
                    importType={'JSON'}
                />
            )}
            {showBulkAssignModal &&
                (notEdited ? (
                    <BulkAssign
                        azOptions={fields[AVAILABILITY_ZONES_STEP_ID].fields[AVAILABILITY_ZONES.ID].value.map((az) => {
                            return {
                                label: az.fields[AVAILABILITY_ZONE_FIELDS.NAME.ID].value,
                                value: az.id
                            };
                        })}
                        wallOptions={fields[SF_NODES_STEP_ID].fields[SILVERFORT_NODES.ID].value.map((node) => {
                            let az = 'unassigned';
                            fields[AVAILABILITY_ZONES_STEP_ID].fields[AVAILABILITY_ZONES.ID].value.forEach(
                                (azFields) => {
                                    if (
                                        azFields.fields[AVAILABILITY_ZONE_FIELDS.SF_NODES.ID].value.includes(
                                            node[NODE_ID.ID].value
                                        )
                                    ) {
                                        az = azFields.fields[AVAILABILITY_ZONE_FIELDS.NAME.ID].value;
                                    }
                                }
                            );
                            return {
                                id: node[NODE_ID.ID].value,
                                name: node[NODE_NAME.ID].value,
                                domains: node[DOMAINS.ID].value.map((domainFields) => domainFields[DOMAIN.ID].value),
                                az
                            };
                        })}
                        dcOptions={[]}
                        onCancel={() => setShowBulkAssignModal(false)}
                        onSave={(selectedAz, wallsToMove, dcsToMove) => {
                            dispatch({
                                type: availabilityZonesActions.MOVE_ENTITIES_TO_AZ,
                                payload: {
                                    entitiesToMove: wallsToMove.map((wall) => wall.id),
                                    azGuid: selectedAz,
                                    type: AZ_ENTITY_TYPES.WALL
                                }
                            });
                            setShowBulkAssignModal(false);
                        }}
                        getDomainByAzGuid={(azGuid) => {
                            const chosenAz = fields[AVAILABILITY_ZONES_STEP_ID].fields[
                                AVAILABILITY_ZONES.ID
                            ].value.filter((az) => {
                                return az.guid === azGuid;
                            })[0];
                            if (chosenAz) {
                                return chosenAz.fields[AVAILABILITY_ZONE_FIELDS.PROTECTED_DOMAINS.ID].value;
                            }
                            return '';
                        }}
                        getEntityIdsByAzGuid={(azGuid) => {
                            const chosenAz = fields[AVAILABILITY_ZONES_STEP_ID].fields[
                                AVAILABILITY_ZONES.ID
                            ].value.filter((az) => {
                                return az.guid === azGuid;
                            })[0];
                            if (chosenAz) {
                                return {
                                    dcs: chosenAz.fields[AVAILABILITY_ZONE_FIELDS.DCS.ID].value,
                                    walls: chosenAz.fields[AVAILABILITY_ZONE_FIELDS.SF_NODES.ID].value
                                };
                            }
                            return { dcs: [], walls: [] };
                        }}
                    />
                ) : (
                    <ConfirmationMessage
                        disableCloseButton={true}
                        title={`bulk assign`}
                        OnApprove={() => setShowBulkAssignModal(false)}
                        body={'Please save your changes before attempting to bulk assign'}
                        OnHide={() => setShowBulkAssignModal(false)}
                        hideCancelButton={true}
                    />
                ))}
        </Modal>
    );
};

export default Wizard;
